import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { COUPON } from "@/data/products";

const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
    const [items, setItems] = useState(() => {
        try {
            const raw = localStorage.getItem("of-cart");
            return raw ? JSON.parse(raw) : [];
        } catch {
            return [];
        }
    });
    const [open, setOpen] = useState(false);
    // Coupon: auto-applied when there is at least one item in the cart.
    const [couponApplied, setCouponApplied] = useState(true);

    useEffect(() => {
        localStorage.setItem("of-cart", JSON.stringify(items));
    }, [items]);

    const keyOf = (id, size) => `${id}__${size}`;

    const addItem = (product, packSize, quantity = 1) => {
        setItems((prev) => {
            const k = keyOf(product.id, packSize);
            const existing = prev.find((p) => keyOf(p.product_id, p.pack_size) === k);
            if (existing) {
                return prev.map((p) =>
                    keyOf(p.product_id, p.pack_size) === k
                        ? { ...p, quantity: p.quantity + quantity }
                        : p,
                );
            }
            return [
                ...prev,
                {
                    product_id: product.id,
                    product_name: product.name,
                    pack_size: packSize,
                    unit_price: product.prices[packSize],
                    quantity,
                    image: product.image,
                },
            ];
        });
    };

    const updateQuantity = (id, size, qty) => {
        setItems((prev) =>
            prev
                .map((p) =>
                    keyOf(p.product_id, p.pack_size) === keyOf(id, size)
                        ? { ...p, quantity: Math.max(1, qty) }
                        : p,
                )
                .filter((p) => p.quantity > 0),
        );
    };

    const removeItem = (id, size) => {
        setItems((prev) => prev.filter((p) => keyOf(p.product_id, p.pack_size) !== keyOf(id, size)));
    };

    const clearCart = () => setItems([]);

    const subtotal = useMemo(
        () => items.reduce((sum, it) => sum + it.unit_price * it.quantity, 0),
        [items],
    );

    const discount = useMemo(
        () => (couponApplied && items.length > 0 ? Math.round(subtotal * COUPON.discount) : 0),
        [couponApplied, items.length, subtotal],
    );

    const total = useMemo(() => Math.max(0, subtotal - discount), [subtotal, discount]);

    const totalCount = useMemo(
        () => items.reduce((sum, it) => sum + it.quantity, 0),
        [items],
    );

    return (
        <CartContext.Provider
            value={{
                items,
                open,
                setOpen,
                addItem,
                updateQuantity,
                removeItem,
                clearCart,
                subtotal,
                discount,
                total,
                totalCount,
                coupon: COUPON,
                couponApplied,
                setCouponApplied,
            }}
        >
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => {
    const ctx = useContext(CartContext);
    if (!ctx) throw new Error("useCart must be used within CartProvider");
    return ctx;
};
